import {
  PwmNoteTypeCreateDocument,
  PwmNoteTypeDeleteDocument,
  PwmNoteTypeGetByIdDocument,
  PwmNoteTypeListDocument,
  PwmNoteTypeUpdateDocument,
  PwmNoteTypesPaginatedDocument,
} from "api/generated";
import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import { ResourceEnum, ScopeEnum } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";
import {
  ICreateNoteTypeVars,
  IGetNoteTypeByIdReturnType,
  IGetNotesListArgs,
  INoteTypeModel,
  INotesListReturnType,
  IUpdateNoteTypeVars,
} from "api";

export async function getPwmNoteTypesList(
  variables: IGetNotesListArgs
): Promise<INotesListReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmNoteTypesPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.pwmNoteTypesPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createPwmNoteType(
  variables: ICreateNoteTypeVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmNoteTypeCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict all note type lists
    executeEviction(client, {
      typename: "PwmNoteType",
      fieldNames: ["pwmNoteTypesPaginated", "pwmNoteTypeList"],
    });

    return res?.data?.pwmNoteTypeCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updatePwmNoteType(
  variables: IUpdateNoteTypeVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmNoteTypeUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict updated entity + all lists + getById
    executeEviction(client, {
      typename: "PwmNoteType",
      id: variables.noteTypeId.toString(),
      fieldNames: [
        "pwmNoteTypesPaginated",
        "pwmNoteTypeList",
        "pwmNoteTypeGetById",
      ],
    });

    return res?.data?.pwmNoteTypeUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function deletePwmNoteType(noteTypeId: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmNoteTypeDeleteDocument,
      variables: {
        noteTypeId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict deleted entity + all lists
    executeEviction(client, {
      typename: "PwmNoteType",
      id: noteTypeId.toString(),
      fieldNames: ["pwmNoteTypesPaginated", "pwmNoteTypeList"],
    });

    return Boolean(res?.data?.pwmNoteTypeDelete);
  } catch (error) {
    throw error;
  }
}

export async function getPwmNoteType(
  noteTypeId: number
): Promise<IGetNoteTypeByIdReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmNoteTypeGetByIdDocument,
      variables: {
        noteTypeId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmNoteTypeGetById;
  } catch (error) {
    throw error;
  }
}

export async function getPwmNoteTypes(
  resource: ResourceEnum,
  scope: ScopeEnum
): Promise<Pick<INoteTypeModel, "noteTypeId" | "name">[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmNoteTypeListDocument,
      variables: {
        resource,
        scope,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.pwmNoteTypeList ?? [];
  } catch (error) {
    throw error;
  }
}
